import { useCallback, useEffect, useState } from 'react'

/**
 * R7 · U540/U541 — the ONE appearance preference.
 *
 * Before this module the stored theme was applied only when the shell's
 * `ThemeToggle` happened to mount, so the landing/auth surfaces ignored it and
 * the choice was a binary light/dark with no way to follow the OS. The control
 * now lives in `/c/settings` (Language & region), and the preference is applied
 * once at boot from `main.tsx` for every surface.
 *
 * Amendment 6 still holds: LIGHT is the product default. `system` is an
 * explicit, deliberate opt-in — an unset preference reads as light, and the OS
 * dark setting alone never selects dark.
 */
export type ThemeChoice = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'takeed-community-theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

/** The stored choice. Unset — and the legacy value `system` written by older
 * builds is indistinguishable from a deliberate one, so it is honoured. */
export function readThemeChoice(): ThemeChoice {
  if (typeof window === 'undefined') return 'light'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'dark' || stored === 'light' || stored === 'system') return stored
  return 'light'
}

/** The theme the choice actually resolves to right now. */
export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light'
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

function apply(choice: ThemeChoice) {
  document.documentElement.setAttribute('data-theme', resolveTheme(choice))
}

/**
 * Apply the stored preference and keep following the OS while — and only
 * while — the member has explicitly chosen `system`. Call once at boot.
 */
export function initTheme(): void {
  if (typeof window === 'undefined') return
  apply(readThemeChoice())
  if (typeof window.matchMedia !== 'function') return
  const media = window.matchMedia(DARK_QUERY)
  const onChange = () => {
    if (readThemeChoice() === 'system') apply('system')
  }
  if (typeof media.addEventListener === 'function') media.addEventListener('change', onChange)
}

/** The settings control's binding: the stored choice + a setter that persists
 * and applies it immediately (no reload, no flash). */
export function useThemeChoice(): [ThemeChoice, (next: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(readThemeChoice)

  // Follow the OS live while `system` is the active choice.
  useEffect(() => {
    if (choice !== 'system') return
    if (typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(DARK_QUERY)
    const onChange = () => apply('system')
    if (typeof media.addEventListener !== 'function') return
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [choice])

  const set = useCallback((next: ThemeChoice) => {
    window.localStorage.setItem(STORAGE_KEY, next)
    apply(next)
    setChoice(next)
  }, [])

  return [choice, set]
}
